<template>
	<div>
		<Card class="eventHomeCard" no-body>
			<div class="m-3">
				<b-badge
					:variant="isExhibitor ? 'primary' : 'success'"
					class="mr-1 product-badge"
				>
					{{ isExhibitor ? 'exhibitor' : 'attendee' }}
				</b-badge>
				<b-badge
					v-if="productTypeLabel"
					:variant="accountLevelBadgeVariant"
					class="mr-1 product-badge"
				>
					{{ productTypeLabel }}
				</b-badge>
				<b-badge v-else variant="secondary" class="mr-1 product-badge">
					add-on
				</b-badge>
				<b-badge v-if="product.allowMultiple" variant="info" class="mr-1 product-badge">
					multi
				</b-badge>
				<b-badge
					v-if="dataSetLabel"
					variant="dark"
					class="product-badge"
				>
					{{ dataSetLabel }}
				</b-badge>
				<h4 class="mt-2">{{ product.productTitle }}</h4>
				<p
					class="description mb-2"
					style="
						overflow: hidden;
						text-overflow: ellipsis;
						display: -webkit-box;
						-webkit-line-clamp: 3;
						-webkit-box-orient: vertical;
					"
				>
					{{ product.productDescription }}
				</p>
				<p class="m-0">
					<strong>{{ amountToDollars(displayAmount) }}</strong>
					<small v-if="product.discountedAmount" class="text-muted ml-2">
						<s>{{ amountToDollars(product.amount) }}</s>
					</small>
				</p>
				<div class="dropdown-divider"></div>
				<BaseButton block type="primary" class="mt-3" @click="$emit('edit', product)">
					Edit
				</BaseButton>
				<BaseButton block type="secondary" outline @click="askArchive">
					Archive
				</BaseButton>
				<BaseButton block type="danger" outline @click="askDelete">
					Delete
				</BaseButton>
			</div>
		</Card>
		<QuestionModal
			:show="showDeleteModal"
			:question="deletePrompt"
			@close="showDeleteModal = false"
			@noClicked="showDeleteModal = false"
			@yesClicked="confirmDelete"
		/>
		<QuestionModal
			:show="showArchiveModal"
			:question="archivePrompt"
			@close="showArchiveModal = false"
			@noClicked="showArchiveModal = false"
			@yesClicked="confirmArchive"
		/>
	</div>
</template>
<script>
import Card from '../../../components/Cards/Card.vue';
import BaseButton from '../../../components/BaseButton.vue';
import QuestionModal from '../../../components/Modals/QuestionModal.vue';

export default {
	name: 'ConferenceProductCard',
	components: { Card, BaseButton, QuestionModal },
	props: {
		product: { type: Object, required: true },
		// Array of { id, title, imageUrl } from account/appInformation.dataSet.
		// Used to resolve product.dataSet (the id string) to a display label.
		// Optional — when empty/missing the conference badge simply hides.
		dataSets: { type: Array, default: () => [] },
	},
	data() {
		return { showDeleteModal: false, showArchiveModal: false };
	},
	computed: {
		isExhibitor() {
			return (
				this.product.checkoutAudience === 'exhibitor' ||
				this.product.sourceCollection === 'exhibitorProducts'
			);
		},
		productTypeLabel() {
			if (this.isExhibitor) return this.product.exhibitorProductType || 'other';
			return this.accountLevelLabel;
		},
		dataSetLabel() {
			if (!this.product.dataSet) return null;
			const match = this.dataSets.find((d) => d && d.id === this.product.dataSet);
			// Fall back to the raw id if we have a dataSet on the product but
			// the appInformation list doesn't include it (legacy / out of sync).
			return match ? match.title || match.id : this.product.dataSet;
		},
		// Items with an accountLevel grant attendee access (they're the "passes"
		// from the backend's perspective). Items without one are add-ons.
		accountLevelLabel() {
			const lvl = this.product.accountLevel;
			if (lvl == null || lvl === '') return null;
			return String(lvl);
		},
		accountLevelBadgeVariant() {
			switch (this.productTypeLabel) {
				case 'gold':
					return 'warning';
				case 'silver':
					return 'secondary';
				case 'bronze':
					return 'danger';
				default:
					return 'primary';
			}
		},
		displayAmount() {
			return this.product.discountedAmount != null
				? this.product.discountedAmount
				: this.product.amount;
		},
		deletePrompt() {
			return `Delete "${this.product.productTitle}"? This cannot be undone.`;
		},
		archivePrompt() {
			const collectionName = this.isExhibitor
				? 'archive_exhibitorProducts'
				: 'archive_products';
			return `Archive "${this.product.productTitle}"? It will be hidden from the cart but kept in the ${collectionName} collection.`;
		},
	},
	methods: {
		amountToDollars(cents) {
			if (cents == null) return '$0.00';
			return Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
			}).format(cents / 100);
		},
		askDelete() {
			this.showDeleteModal = true;
		},
		confirmDelete() {
			this.showDeleteModal = false;
			this.$emit('delete', this.product);
		},
		askArchive() {
			this.showArchiveModal = true;
		},
		confirmArchive() {
			this.showArchiveModal = false;
			this.$emit('archive', this.product);
		},
	},
};
</script>

<style scoped>
/* Default b-badge sizing renders the product-type label small and hard to
   scan at a glance in the grid. Bump font size, weight, and contrast so
   "pass" / "showcase" / "addon" / "donation" reads clearly. */
.product-badge {
	font-size: 0.75rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	padding: 0.35em 0.65em;
	text-transform: uppercase;
}
</style>
